import type { ComponentRenderProps } from "@json-render/react";
import { z } from "zod";

import { REPORT_DATE_FORMATS } from "@/components/ui/report/ReportDate/ReportDate";
import {
  parseReportSpacing,
  parseReportTextStyle,
  REPORT_SPACING_DESCRIPTION,
  REPORT_TEXT_STYLE_DESCRIPTION,
  reportSpacingSchema,
  reportTextStyleSchema,
} from "@/components/ui/report/reportPresentation";

import { ReportHeader } from "./ReportHeader";

const reportHeaderDateFormatSchema = z.enum(REPORT_DATE_FORMATS);

/** Layout Studio catalog definition for ReportHeader. */
export const ReportHeaderSpec = {
  props: z.object({
    briefingTitle: z.string().nullable().optional(),
    dateFormat: reportHeaderDateFormatSchema.nullable().optional(),
    titleStyle: reportTextStyleSchema.nullable().optional(),
    spacing: reportSpacingSchema.nullable().optional(),
  }),
  description: `Report page header: briefing title and automatically supplied scheduled briefing date. Place at the top of report layouts. briefingTitle is the main heading; omit or null for the default placeholder. dateFormat controls display (monthDayYear or dayAbbrevMonthYear); omit for monthDayYear. Optional titleStyle sets title typography (${REPORT_TEXT_STYLE_DESCRIPTION}); its textAlign also aligns the date line. Optional spacing sets header padding and the title-to-date gap (${REPORT_SPACING_DESCRIPTION}). Omit titleStyle and spacing for the default header. For preview placeholder copy when the user did not supply a title, use briefingTitle Briefing title — not Lorem ipsum or realistic news headlines.`,
  example: {
    briefingTitle: "Briefing title",
    dateFormat: "monthDayYear",
  },
} as const;

type ReportHeaderPropsFromSpec = {
  briefingTitle?: string | null;
  dateValue?: string | null;
  dateFormat?: (typeof REPORT_DATE_FORMATS)[number] | null;
  titleStyle?: unknown;
  spacing?: unknown;
};

/** Adapter for json-render defineRegistry: renders ReportHeader from catalog props. */
export const ReportHeaderFromProps = ({ element }: ComponentRenderProps) => {
  const { briefingTitle, dateValue, dateFormat, titleStyle, spacing } =
    element.props as ReportHeaderPropsFromSpec;

  return (
    <ReportHeader
      briefingTitle={briefingTitle ?? undefined}
      dateValue={dateValue ?? undefined}
      dateFormat={dateFormat ?? undefined}
      titleStyle={parseReportTextStyle(titleStyle)}
      spacing={parseReportSpacing(spacing)}
    />
  );
};
